// src/components/trading/ExecutionTimingSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Info, Clock, Zap, Eye, Crown, Bolt } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAccountType } from '@/hooks/useAccountType';
import { ExecutionTimingSettings } from './types';

interface ExecutionTimingSettingsProps {
  settings: ExecutionTimingSettings;
  onSettingsChange: (changes: Partial<ExecutionTimingSettings>) => void;
  selectedModels?: string[];
}

// Default settings to use if none are provided
const defaultSettings: ExecutionTimingSettings = {
  signalFrequency: 'daily_close',
  monitorFrequency: '5min',
  executeRthOnly: true,
  allowPrePostMarket: false,
};

// Renamed export to avoid conflicts if imported elsewhere directly
export const ExecutionTimingSettingsComponent: React.FC<ExecutionTimingSettingsProps> = ({
  settings = {} as ExecutionTimingSettings,
  onSettingsChange,
  selectedModels = [],
}) => {
  // Get account type and admin status for role-based restrictions
  const { accountType, isAdministrator } = useAccountType();
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await isAdministrator();
      setIsAdmin(adminStatus);
    };
    
    if (accountType === 'enterprise') {
      checkAdminStatus();
    }
  }, [accountType, isAdministrator]);

  // Maintain local state in sync with props to ensure UI reflects current values
  const [localSettings, setLocalSettings] = useState<ExecutionTimingSettings>({
    ...defaultSettings,
    ...settings
  });
  
  // Update local state when props change
  useEffect(() => {
    setLocalSettings(prevSettings => ({
      ...prevSettings,
      ...settings
    }));
  }, [settings]);

  // Helper function to safely update settings
  const handleSettingChange = useCallback((field: keyof ExecutionTimingSettings, value: any) => {
    // Update local state immediately for responsive UI
    const newLocalSettings = {
      ...localSettings,
      [field]: value
    };
    setLocalSettings(newLocalSettings);
    
    // Notify parent component with the COMPLETE settings object, not just the changed field
    if (onSettingsChange && typeof onSettingsChange === 'function') {
      // Send the complete updated settings object to ensure all fields are preserved
      onSettingsChange(newLocalSettings);
    }
  }, [onSettingsChange, localSettings]);

  // Role-based access checks
  const hasEnterpriseAccess = accountType === 'enterprise' || isAdmin;
  const hasHighFrequencyAccess = hasEnterpriseAccess;

  const disableHighFrequencySignal = selectedModels.length > 0 &&
                                     selectedModels.every(m => ['arima', 'monte_carlo'].includes(m));

  const disableTickMonitoring = !selectedModels.some(m => ['stewie_quant'].includes(m));

  // Enterprise badge component
  const EnterpriseBadge = () => (
    <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs ml-2">
      <Crown className="w-3 h-3 mr-1" />
      Enterprise
    </Badge>
  );

  // HFT badge component
  const HFTBadge = () => (
    <Badge className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs ml-2">
      <Bolt className="w-3 h-3 mr-1" />
      HFT
    </Badge>
  );

  // Ultra-HFT badge component for sub-second frequencies
  const UltraHFTBadge = () => (
    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs ml-2">
      <Bolt className="w-3 h-3 mr-1" />
      Ultra-HFT
    </Badge>
  );

  return (
    <div className="space-y-6">

      {/* Signal Evaluation Frequency Section */}
      <div>
        <h3 className="text-base font-medium mb-4 flex items-center gap-2">
          <Zap className="text-blue-600" size={18} />
          Signal Evaluation Frequency
          {hasHighFrequencyAccess && (
            <Badge variant="outline" className="text-xs">
              <Bolt className="w-3 h-3 mr-1" />
              HFT Enabled
            </Badge>
          )}
        </h3>
        <div className="grid grid-cols-4 items-center gap-2 sm:gap-4">
          <Label htmlFor="signal-frequency" className="text-right text-sm">Frequency</Label>
          <div className="col-span-3 flex items-center gap-2">
            <Select
              value={localSettings.signalFrequency}
              onValueChange={(value) => handleSettingChange('signalFrequency', value)}
            >
              <SelectTrigger id="signal-frequency" className="rounded-md">
                <SelectValue placeholder="Select how often signals are generated" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily_close">Daily (at Market Close)</SelectItem>
                <SelectItem value="daily_open">Daily (at Market Open)</SelectItem>
                <SelectItem value="hourly">Hourly (Top of Hour)</SelectItem>
                <SelectItem value="15min" disabled={disableHighFrequencySignal}>15 Minutes</SelectItem>
                <SelectItem value="5min" disabled={disableHighFrequencySignal}>5 Minutes</SelectItem>
                <SelectItem value="1min" disabled={disableHighFrequencySignal}>1 Minute</SelectItem>
                <SelectItem value="continuous" disabled={disableHighFrequencySignal}>Continuous (60 seconds)</SelectItem>
                
                {/* Enterprise-only sub-second frequencies */}
                {hasHighFrequencyAccess ? (
                  <>
                    <Separator className="my-2" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Crown className="w-3 h-3" />
                        Enterprise High-Frequency Trading
                      </div>
                    </div>
                    <SelectItem value="30sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>30 Seconds</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="10sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>10 Seconds</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="5sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>5 Seconds</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="1sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>1 Second</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <Separator className="my-1" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-purple-600 font-medium">
                        <Bolt className="w-3 h-3" />
                        Ultra-High-Frequency Trading
                      </div>
                    </div>
                    <SelectItem value="500ms" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>500 Milliseconds</span>
                        <UltraHFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="100ms" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>100 Milliseconds</span>
                        <UltraHFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="tick_ultra" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Tick-by-Tick Ultra-HFT</span>
                        <UltraHFTBadge />
                      </div>
                    </SelectItem>
                  </>
                ) : (
                  <>
                    <Separator className="my-2" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Crown className="w-3 h-3" />
                        Enterprise-Only Frequencies
                      </div>
                    </div>
                    <SelectItem value="30sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>30 Seconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="10sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>10 Seconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="5sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>5 Seconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="1sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>1 Second</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <Separator className="my-1" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Bolt className="w-3 h-3" />
                        Ultra-High-Frequency Trading
                      </div>
                    </div>
                    <SelectItem value="500ms" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>500 Milliseconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="100ms" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>100 Milliseconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="tick_ultra" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Tick-by-Tick Ultra-HFT</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                  <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-4 w-4 p-0 ml-1 cursor-help align-middle shrink-0">
                           <Info className="h-3 w-3 text-gray-400" />
                      </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px] text-xs">
                  <div className="space-y-2">
                    <p>Determines how often the strategy's core models re-evaluate the market and generate new potential trade signals.</p>
                    {!hasHighFrequencyAccess && (
                      <p className="text-amber-600 font-medium">
                        <Crown className="w-3 h-3 inline mr-1" />
                        Sub-second frequencies require Enterprise account
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2 col-span-4 pl-[calc(25%+1rem)]">
           {!hasHighFrequencyAccess ? (
             <>Note: Some frequencies may be disabled based on selected models. Sub-second and ultra-HFT frequencies require Enterprise account.</>
           ) : (
             <>Note: Some frequencies may be disabled based on selected models. Ultra-HFT frequencies (≤500ms) require dedicated low-latency infrastructure.</>
           )}
        </p>
      </div>

      <Separator />

      {/* Position Monitoring Frequency Section */}
      <div>
        <h3 className="text-base font-medium mb-4 flex items-center gap-2">
          <Eye className="text-blue-600" size={18} />
          Position Monitoring & Exits
          {hasHighFrequencyAccess && (
            <Badge variant="outline" className="text-xs">
              <Bolt className="w-3 h-3 mr-1" />
              HFT Enabled
            </Badge>
          )}
        </h3>
        <div className="grid grid-cols-4 items-center gap-2 sm:gap-4">
          <Label htmlFor="monitor-frequency" className="text-right text-sm">Check Freq.</Label>
          <div className="col-span-3 flex items-center gap-2">
            <Select
              value={localSettings.monitorFrequency}
              onValueChange={(value) => handleSettingChange('monitorFrequency', value)}
            >
              <SelectTrigger id="monitor-frequency" className="rounded-md">
                <SelectValue placeholder="Select how often positions are checked" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="matches_signal">Matches Signal Frequency</SelectItem>
                <SelectItem value="1min">Every 1 Minute</SelectItem>
                <SelectItem value="5min">Every 5 Minutes</SelectItem>
                <SelectItem value="15min">Every 15 Minutes</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="tick" disabled={disableTickMonitoring}>Tick-by-Tick (*Requires compatible model*)</SelectItem>
                
                {/* Enterprise-only high-frequency monitoring */}
                {hasHighFrequencyAccess ? (
                  <>
                    <Separator className="my-2" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Crown className="w-3 h-3" />
                        Enterprise High-Frequency Monitoring
                      </div>
                    </div>
                    <SelectItem value="30sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 30 Seconds</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="10sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 10 Seconds</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="5sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 5 Seconds</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="1sec" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 1 Second</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="tick_hft" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Tick-by-Tick HFT</span>
                        <HFTBadge />
                      </div>
                    </SelectItem>
                    <Separator className="my-1" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-purple-600 font-medium">
                        <Bolt className="w-3 h-3" />
                        Ultra-High-Frequency Monitoring
                      </div>
                    </div>
                    <SelectItem value="500ms" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 500 Milliseconds</span>
                        <UltraHFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="100ms" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 100 Milliseconds</span>
                        <UltraHFTBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="tick_ultra" className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Tick-by-Tick Ultra-HFT</span>
                        <UltraHFTBadge />
                      </div>
                    </SelectItem>
                  </>
                ) : (
                  <>
                    <Separator className="my-2" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Crown className="w-3 h-3" />
                        Enterprise-Only Monitoring
                      </div>
                    </div>
                    <SelectItem value="30sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 30 Seconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="10sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 10 Seconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="5sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 5 Seconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="1sec" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 1 Second</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="tick_hft" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Tick-by-Tick HFT</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <Separator className="my-1" />
                    <div className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Bolt className="w-3 h-3" />
                        Ultra-High-Frequency Monitoring
                      </div>
                    </div>
                    <SelectItem value="500ms" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 500 Milliseconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="100ms" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Every 100 Milliseconds</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                    <SelectItem value="tick_ultra" disabled className="pl-6">
                      <div className="flex items-center justify-between w-full">
                        <span>Tick-by-Tick Ultra-HFT</span>
                        <EnterpriseBadge />
                      </div>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
             <TooltipProvider delayDuration={100}>
              <Tooltip>
                  <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-4 w-4 p-0 ml-1 cursor-help align-middle shrink-0">
                           <Info className="h-3 w-3 text-gray-400" />
                      </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[300px] text-xs">
                  <div className="space-y-2">
                    <p>Determines how often existing positions are checked against profit targets, stop losses, and other exit conditions. Can be faster than signal frequency.</p>
                    {!hasHighFrequencyAccess && (
                      <p className="text-amber-600 font-medium">
                        <Crown className="w-3 h-3 inline mr-1" />
                        Sub-second monitoring requires Enterprise account
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        {!hasHighFrequencyAccess && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <Crown className="w-4 h-4" />
              <span className="font-medium">Enterprise High-Frequency Trading</span>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              Unlock sub-second execution frequencies (500ms, 100ms) and ultra-HFT tick-by-tick monitoring with an Enterprise account. 
              Perfect for algorithmic trading firms, market makers, and high-frequency strategies.
            </p>
          </div>
        )}
      </div>

      <Separator />

       {/* Execution Window Section */}
      <div>
        <h3 className="text-base font-medium mb-4 flex items-center gap-2">
            <Clock className="text-blue-600" size={18} />
            Execution Window
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50/50">
                <Label htmlFor="rth-only" className="text-sm font-medium text-gray-700 flex-1 cursor-pointer pr-2">
                    Execute only during Regular Trading Hours (RTH)
                </Label>
                <Switch id="rth-only" checked={localSettings.executeRthOnly} onCheckedChange={(checked) => handleSettingChange('executeRthOnly', checked)} />
            </div>
            <div className={`flex items-center justify-between p-3 border rounded-lg bg-gray-50/50 ${localSettings.executeRthOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <Label htmlFor="allow-outside-rth" className={`text-sm font-medium flex-1 pr-2 ${localSettings.executeRthOnly ? 'text-gray-400' : 'text-gray-700 cursor-pointer'}`}>
                    Allow Pre/Post Market Execution
                </Label>
                <Switch id="allow-outside-rth" checked={localSettings.allowPrePostMarket} onCheckedChange={(checked) => handleSettingChange('allowPrePostMarket', checked)} disabled={localSettings.executeRthOnly} />
            </div>
        </div>
         <p className="text-xs text-gray-500 mt-2">
           Configure whether trades can be initiated or closed outside standard market hours.
           {hasHighFrequencyAccess && (
             <> High-frequency strategies may benefit from extended hours execution.</>
           )}
        </p>
      </div>

    </div>
  );
};

// Make sure the export name matches how it's imported in EnhancedStrategyDialog
export { ExecutionTimingSettingsComponent as ExecutionTimingSettings };
